import { Link } from "@/lib/router";
import { ArrowRight, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { FloatingLetters, IconTile, Mascot, type Tone } from "@/components/app/Playful";
import { cn } from "@/lib/utils";
import { PricingCards } from "./Pricing";
import { SiteFooter } from "@/components/app/SiteFooter";
import { SiteStats } from "@/components/app/SiteStats";
import { StoryCover } from "@/components/app/StoryCover";
import { stories } from "@/data/stories";
import { tirthankaras } from "@/data/tirthankar";
import { wordImages } from "@/data/word-images";
import { FREE_STORIES } from "@/lib/catalog";

/** Public landing page for signed-out visitors. English only by design. */

const slides = [
  {
    src: "/slides/grammar.webp",
    title: "A world of grammar",
    text: "Nineteen colourful topics — from the alphabet to tenses, cases, compounds and dialects.",
  },
  {
    src: "/slides/present.webp",
    title: "Clear suffix tables",
    text: "See how every ending changes with person and number.",
  },
  {
    src: "/slides/games.webp",
    title: "Learn by playing",
    text: "29 games built from real Prakrit words, sentences and grammar.",
  },
  {
    src: "/slides/dictionary.webp",
    title: "55,000-word dictionary",
    text: "Search in Prakrit, Hindi or Roman letters and hear every word.",
  },
  {
    src: "/slides/sentences.webp",
    title: "Build sentences",
    text: "Put words in the right order and check yourself instantly.",
  },
  {
    src: "/slides/alphabet.webp",
    title: "Start with the alphabet",
    text: "Vowels, consonants and marks — the perfect first step.",
  },
];

const features: { emoji: string; tone: Tone; title: string; text: string }[] = [
  {
    emoji: "🔤",
    tone: "lagoon",
    title: "Alphabet first",
    text: "Begin with the Prakrit letters, then move to real words.",
  },
  {
    emoji: "☀️",
    tone: "mint",
    title: "Grammar made friendly",
    text: "Tables, formulas and examples you can tap and hear.",
  },
  {
    emoji: "🎮",
    tone: "pink",
    title: "29 games",
    text: "Matching, meanings, word order, forms and more.",
  },
  {
    emoji: "🔍",
    tone: "sky",
    title: "Big dictionary",
    text: "55,000 Prakrit entries with Hindi and English meanings.",
  },
  {
    emoji: "🔊",
    tone: "mango",
    title: "Hear it spoken",
    text: "Every word read aloud — with the natural Bhashini Sanskrit voice for members.",
  },
  {
    emoji: "⭐",
    tone: "purple",
    title: "XP and streaks",
    text: "Earn XP, keep your streak and climb the leaderboard.",
  },
  {
    emoji: "🌳",
    tone: "peach",
    title: "50+ picture stories",
    text: "Fables and Agama tales in simple Prakrit, each with pictures, words and a quiz.",
  },
  {
    emoji: "🪷",
    tone: "lavender",
    title: "24 Tirthankaras",
    text: "Their names, emblems, colours and birthplaces, with the लोगस्स gathas.",
  },
];

const faqs = [
  {
    q: "Is there a free plan?",
    a: "Yes. The alphabet, present-tense lessons, three games, three sentence challenges, two picture stories, the 24 Tirthankaras page and the dictionary are free forever.",
  },
  {
    q: "How do I pay?",
    a: "Online payment is coming soon. Until then, choose a plan after signing up and an admin will activate your membership.",
  },
  {
    q: "Who is it for?",
    a: "Children, students and adults who want to read and understand Prakrit, step by step.",
  },
];

export function Landing() {
  return (
    <div lang="en" className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-white/60 bg-background/85 backdrop-blur">
        <nav className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3" aria-label="Main">
          <Link to="/" className="flex items-center gap-2" aria-label="Prakrit Sarita home">
            <img src="/prakrit-sarita-icon.png" alt="" className="size-10" />
            <span className="hidden text-lg font-extrabold sm:inline">Prakrit Sarita</span>
          </Link>
          <div className="ml-auto hidden items-center gap-1 sm:flex">
            <a
              href="#features"
              className="rounded-full px-4 py-2 text-sm font-bold text-muted-foreground hover:text-foreground"
            >
              Features
            </a>
            <a
              href="#pricing"
              className="rounded-full px-4 py-2 text-sm font-bold text-muted-foreground hover:text-foreground"
            >
              Pricing
            </a>
          </div>
          <div className="ml-auto flex items-center gap-2 sm:ml-2">
            <Button asChild variant="ghost">
              <Link to="/auth">Log in</Link>
            </Button>
            <Button asChild>
              <Link to="/signup">Sign up free</Link>
            </Button>
          </div>
        </nav>
      </header>

      <main>
        {/* Hero */}
        <section className="relative isolate overflow-hidden">
          <FloatingLetters
            letters={["अ", "प्रा", "कृ", "ण", "इ"]}
            className="-z-10 hidden md:block"
          />
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 md:grid-cols-[1.1fr_0.9fr] md:py-20">
            <div>
              <img
                src="/prakrit-sarita-logo.webp"
                alt="प्राकृत सरिता · Prakrit Sarita"
                className="mb-6 h-auto w-52"
              />
              <h1 className="text-4xl font-extrabold leading-tight sm:text-5xl md:text-6xl">
                Learn Prakrit the{" "}
                <span className="bg-candy bg-clip-text text-transparent">playful</span> way
              </h1>
              <p className="mt-5 max-w-xl text-lg text-muted-foreground">
                Alphabet, grammar, 50+ picture stories, a 55,000+ word dictionary and 29 games, in
                short, friendly lessons with real pronunciation. Start free, no card needed.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="shadow-pop">
                  <Link to="/signup">
                    Start learning free <ArrowRight className="ml-1 size-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="#pricing">See pricing</a>
                </Button>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                Already learning?{" "}
                <Link to="/auth" className="font-bold text-primary hover:underline">
                  Log in
                </Link>
              </p>
            </div>
            <div className="relative mx-auto">
              <div className="bubble p-8">
                <Mascot
                  mood="happy"
                  holding="book"
                  className="mx-auto size-56"
                  label="Berry the blueberry"
                />
                <p className="mt-2 text-center font-extrabold">
                  Hi! I'm Berry. Let's learn together!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section aria-labelledby="stats-title" className="mx-auto max-w-6xl px-4 pb-6">
          <h2 id="stats-title" className="sr-only">
            What is inside
          </h2>
          <SiteStats english />
        </section>

        <Slides />

        {/* Features */}
        <section id="features" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-16">
          <h2 className="text-center text-3xl font-extrabold sm:text-4xl">
            Everything you need to read Prakrit
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">
            Built with teachers from the प्राकृत-रचना-सौरभ lessons, checked against the source
            texts.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <article key={f.title} className="bubble flex gap-4 p-5">
                <IconTile tone={f.tone} size="md">
                  {f.emoji}
                </IconTile>
                <div>
                  <h3 className="text-lg font-extrabold">{f.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Stories */}
        <section id="stories" aria-labelledby="stories-title" className="mx-auto max-w-6xl scroll-mt-24 px-4 pb-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 id="stories-title" className="text-3xl font-extrabold sm:text-4xl">
                Story garden
              </h2>
              <p className="mt-2 max-w-xl text-muted-foreground">
                {stories.length} illustrated stories in simple Prakrit. Two are free to read; members get them all.
              </p>
            </div>
            <Button asChild variant="outline">
              <Link to="/stories">
                All stories <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {stories.slice(0, 4).map((story) => {
              const free = FREE_STORIES.includes(story.id);
              return (
                <Link
                  key={story.id}
                  to={free ? "/stories/$storyId" : "/stories"}
                  params={free ? { storyId: story.id } : undefined}
                  className="bubble group overflow-hidden transition hover:-translate-y-0.5"
                >
                  <span className="relative block aspect-[4/3] overflow-hidden">
                    <StoryCover story={story} className="transition duration-500 group-hover:scale-105" />
                    <span
                      className={cn(
                        "absolute left-2 top-2 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-extrabold shadow-sm",
                        free ? "bg-[#e3f9ef] text-[#0d7552]" : "bg-white/90 text-muted-foreground",
                      )}
                    >
                      {free ? "Free" : (
                        <>
                          <Lock className="size-3" /> Members
                        </>
                      )}
                    </span>
                  </span>
                  <span className="block p-3">
                    <span className="prakrit block text-lg font-extrabold">{story.title}</span>
                    <span className="text-xs text-muted-foreground">{story.en}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Tirthankaras and picture words */}
        <section className="mx-auto grid max-w-6xl gap-4 px-4 pb-16 md:grid-cols-2">
          <Link to="/tirthankar" className="bubble group flex flex-col p-6 transition hover:-translate-y-0.5">
            <h2 className="text-2xl font-extrabold">The 24 Tirthankaras</h2>
            <p className="mt-1 text-muted-foreground">Names in Prakrit, emblems, colours and birthplaces. Open to everyone.</p>
            <span className="mt-5 grid grid-cols-4 gap-2">
              {tirthankaras.slice(0, 4).map((t) => (
                <img
                  key={t.id}
                  src={t.murti}
                  alt={t.en}
                  loading="lazy"
                  className="aspect-[3/4] w-full rounded-2xl object-cover transition group-hover:scale-[1.02]"
                />
              ))}
            </span>
            <span className="mt-4 inline-flex items-center gap-1 font-extrabold text-primary">
              Meet the Tirthankaras <ArrowRight className="size-4" />
            </span>
          </Link>
          <Link to="/vocabulary" className="bubble group flex flex-col p-6 transition hover:-translate-y-0.5">
            <h2 className="text-2xl font-extrabold">Picture words</h2>
            <p className="mt-1 text-muted-foreground">
              {wordImages.length} everyday words with friendly pictures. Tap any word to hear it.
            </p>
            <span className="mt-5 grid grid-cols-4 gap-2">
              {wordImages.slice(0, 8).map((w) => (
                <img
                  key={w.id}
                  src={w.image}
                  alt={w.en}
                  loading="lazy"
                  className="aspect-square w-full rounded-2xl object-cover"
                />
              ))}
            </span>
            <span className="mt-4 inline-flex items-center gap-1 font-extrabold text-primary">
              See the word garden <ArrowRight className="size-4" />
            </span>
          </Link>
        </section>

        {/* Pricing */}
        <section id="pricing" className="scroll-mt-24 bg-dreamy py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-center text-3xl font-extrabold sm:text-4xl">Simple pricing</h2>
            <p className="mx-auto mt-3 max-w-xl text-center text-muted-foreground">
              Start free. Upgrade when you're ready for every lesson and game.
            </p>
            <div className="mt-10">
              <PricingCards />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-3xl px-4 py-16">
          <h2 className="text-center text-3xl font-extrabold">Questions</h2>
          <div className="mt-8 space-y-3">
            {faqs.map((f) => (
              <details key={f.q} className="bubble group p-5">
                <summary className="cursor-pointer list-none font-extrabold">{f.q}</summary>
                <p className="mt-2 text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final call */}
        <section className="mx-auto max-w-6xl px-4 pb-20">
          <div className="bg-candy relative overflow-hidden rounded-[2rem] p-8 text-center text-white shadow-pop sm:p-12">
            <h2 className="text-3xl font-extrabold sm:text-4xl">Ready to start?</h2>
            <p className="mx-auto mt-3 max-w-xl text-white/85">
              Create your free account in a minute — with email or Google.
            </p>
            <Button asChild size="lg" variant="secondary" className="mt-6">
              <Link to="/signup">Sign up free</Link>
            </Button>
          </div>
        </section>
      </main>

      <SiteFooter english className="pb-6" />
    </div>
  );
}

function Slides() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = (n: number) => setI((n + slides.length) % slides.length);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (paused || reduce) return;
    const t = setTimeout(() => setI((x) => (x + 1) % slides.length), 5000);
    return () => clearTimeout(t);
  }, [i, paused]);
  const slide = slides[i]!;
  return (
    <section
      aria-roledescription="carousel"
      aria-label="App screenshots"
      className="mx-auto max-w-5xl px-4"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="bubble overflow-hidden p-3 sm:p-4">
        <div className="relative overflow-hidden rounded-[1.25rem] bg-muted">
          {slides.map((s, k) => (
            <img
              key={s.src}
              src={s.src}
              alt={s.title}
              width={1280}
              height={800}
              loading={k === 0 ? "eager" : "lazy"}
              className={cn(
                "h-auto w-full transition-opacity duration-500",
                k === i ? "relative opacity-100" : "absolute inset-0 opacity-0",
              )}
              aria-hidden={k !== i}
            />
          ))}
          <button
            type="button"
            onClick={() => go(i - 1)}
            aria-label="Previous screenshot"
            className="absolute left-3 top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/90 shadow-md hover:bg-white"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            type="button"
            onClick={() => go(i + 1)}
            aria-label="Next screenshot"
            className="absolute right-3 top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/90 shadow-md hover:bg-white"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
        <div
          className="flex flex-col items-center gap-3 px-2 pb-2 pt-4 text-center"
          aria-live="polite"
        >
          <h3 className="text-xl font-extrabold">{slide.title}</h3>
          <p className="text-muted-foreground">{slide.text}</p>
          <div className="flex gap-2" role="tablist" aria-label="Choose screenshot">
            {slides.map((s, k) => (
              <button
                key={s.src}
                type="button"
                role="tab"
                aria-selected={k === i}
                aria-label={s.title}
                onClick={() => go(k)}
                className={cn(
                  "h-2.5 rounded-full transition-all",
                  k === i ? "w-8 bg-primary" : "w-2.5 bg-primary/25",
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
